import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { vectorMin } from '../assets/assets';
import { useQuery } from '@tanstack/react-query';
import { getGigs } from '../services/gig.service';
import SkeletonCard from './SkeletonCard';

const ExploreServicesGrid = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [slidingIndexes, setSlidingIndexes] = useState({});
  const [activeHoverCard, setActiveHoverCard] = useState(null);

  // Fetch gigs using react-query
  const { data, isLoading, isError } = useQuery({
    queryKey: ['gigs', currentPage],
    queryFn: () => getGigs({ page: currentPage, limit: 12 }),
    keepPreviousData: true,
  });

  const gigs = data?.gigs || [];
  const pagination = data?.pagination || {};
  const totalPages = pagination.pages || 1;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Only set up sliding for the card being hovered
    let slidingInterval;
    if (activeHoverCard !== null) {
      slidingInterval = setInterval(() => {
        setSlidingIndexes(prevIndexes => {
          const newIndexes = { ...prevIndexes };
          // Find gig in the fetched data
          const gig = gigs.find(g => g._id === activeHoverCard);
          
          if (gig) {
             // Construct thumbnails array from cover + other thumbnails if available
             // The API response structure for thumbnails might need adjustment based on actual data
             // Assuming gig has cover, firstThumbnail, secondThumbnail etc or a thumbnails array
             // Based on the user request example:
             // "cover": "...", "firstThumbnail": "...", "secondThumbnail": "...", "thirdThumbnail": "..."
             // Let's create a temporary array for sliding logic
             const thumbnails = [gig.cover, gig.firstThumbnail, gig.secondThumbnail, gig.thirdThumbnail].filter(Boolean);

             if (thumbnails.length > 1) {
                const currentIndex = prevIndexes[activeHoverCard] || 0;
                newIndexes[activeHoverCard] = (currentIndex + 1) % thumbnails.length;
             }
          }
          return newIndexes;
        });
      }, 1500); // Change thumbnail every 1.5 seconds while hovering
    }
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
      if (slidingInterval) clearInterval(slidingInterval);
    };
  }, [activeHoverCard, gigs]);

  const getLowestPrice = (pricing) => {
    if (!pricing) return 0;
    // Pricing structure from API: { basic: { price: 50 }, standard: { price: 100 }, ... }
    const prices = Object.values(pricing).map(p => p.price).filter(p => typeof p === 'number');
    return prices.length > 0 ? Math.min(...prices) : 0;
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="w-[70%] lg:w-[50%] mx-auto flex justify-between items-center mt-8 space-x-2 mb-10">
        <button 
          onClick={prevPage}
          className="w-10 h-10 rounded-full border border-[#BCBCBC] flex items-center justify-center text-[#BCBCBC] hover:bg-gray-100 disabled:opacity-50"
          disabled={currentPage === 1}
        >
          <ChevronLeft size={20} />
        </button>
        
        {currentPage > 1 && (
          <button 
            onClick={() => goToPage(1)}
            className={`w-10 h-10 rounded-full flex items-center justify-center ${
              currentPage === 1 ? 'text-blue-700' : ' text-gray-700 hover:bg-gray-100'
            }`}
          >
            1
          </button>
        )}
        
        {currentPage > 3 && <span className="px-2">...</span>}
        
        {currentPage > 2 && (
          <button 
            onClick={() => goToPage(currentPage - 1)}
            className="w-10 h-10 rounded-full flex items-center justify-center text-gray-700 hover:bg-gray-100"
          >
            {currentPage - 1}
          </button>
        )}
        
        <button 
          className="w-10 h-10 rounded-full text-blue-600 flex items-center justify-center"
        >
          {currentPage}
        </button>
        
        {currentPage < totalPages - 1 && (
          <button 
            onClick={() => goToPage(currentPage + 1)}
            className="w-10 h-10 rounded-full flex items-center justify-center text-gray-700 hover:bg-gray-100"
          >
            {currentPage + 1}
          </button>
        )}
        
        {currentPage < totalPages - 2 && <span className="px-2">...</span>}
        
        {currentPage < totalPages && (
          <button 
            onClick={() => goToPage(totalPages)}
            className="w-10 h-10 rounded-full flex items-center justify-center text-gray-700 hover:bg-gray-100"
          >
            {totalPages}
          </button>
        )}
        
        <button 
          onClick={nextPage}
          className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-[#185BC3] hover:bg-gray-100 disabled:opacity-50"
          disabled={currentPage === totalPages}
        >
          <ChevronRight size={20} />
        </button>
      </div>
    );
  };

  // Service Card Component
  const ServiceCard = ({ gig }) => {
    const currentThumbnailIndex = slidingIndexes[gig._id] || 0;
    const lowestPrice = getLowestPrice(gig.pricing);
    
    // Construct thumbnails array
    const thumbnails = [gig.cover, gig.firstThumbnail, gig.secondThumbnail, gig.thirdThumbnail].filter(Boolean);
    const currentImage = thumbnails[currentThumbnailIndex] || gig.cover;

    // Generate initials for avatar fallback
    const getInitials = (firstName, lastName) => {
      if (!firstName && !lastName) return "U";
      return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
    };
    
    const creatorName = gig.creator ? `${gig.creator.firstName} ${gig.creator.lastName}` : "Unknown";
    const creatorInitials = gig.creator ? getInitials(gig.creator.firstName, gig.creator.lastName) : "U";

    return (
      <div 
        className="flex flex-col h-full"
        onMouseEnter={() => setActiveHoverCard(gig._id)}
        onMouseLeave={() => setActiveHoverCard(null)}
      >
        <div className="relative rounded-lg overflow-hidden mb-3 hover:shadow-md transition-shadow duration-300 group">
          <a href={`/gigs/${gig.slug}`}>
            {currentImage ? (
              <img 
                src={currentImage} 
                alt={gig.title} 
                className="w-full h-44 object-cover transition-all duration-500"
              />
            ) : (
              <div className="w-full h-44 bg-gray-200 flex items-center justify-center">
                <span className="text-gray-500">No Image</span>
              </div>
            )}
          </a>
          {thumbnails.length > 1 && (
            <div className="absolute bottom-2 right-2 flex space-x-1">
              {thumbnails.map((_, index) => (
                <span 
                  key={index}
                  className={`w-2 h-2 bg-white rounded-full transition-opacity duration-300 ${
                    index === currentThumbnailIndex ? 'opacity-100' : 'opacity-60'
                  }`}
                ></span>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex items-center mb-2">
          {gig.creator && gig.creator.profilePicture ? (
            <img 
              src={gig.creator.profilePicture} 
              alt={creatorName} 
              className="w-6 h-6 rounded-full mr-2 object-cover"
            />
          ) : (
            <div className="w-6 h-6 rounded-full mr-2 bg-blue-600 flex items-center justify-center text-white text-xs">
              {creatorInitials}
            </div>
          )}
          <span className="text-sm text-gray-600 truncate">{creatorName}</span>
        </div>
        
        <a href={`/gigs/${gig.slug}`} className="hover:text-blue-900 mb-1 block">
          <h3 className="text-sm font-medium line-clamp-2 min-h-[40px]">{gig.title}</h3>
        </a>
        
        <div className="flex items-center mb-1">
          <span className="text-[#000000]">★</span>
          <span className="text-sm font-medium ml-1">
            {gig.starNumber > 0 ? (gig.totalStars / gig.starNumber).toFixed(1) : "0.0"}
          </span>
          <span className="text-sm text-gray-500 ml-1">({gig.sales || 0})</span>
        </div>
        
        <div className="mt-auto pt-2 border-t border-gray-100">
          <p className="text-sm font-semibold">From ${lowestPrice}</p>
        </div>
      </div>
    );
  };

  if (isError) {
    return (
      <div className="text-center py-10 text-red-500">
        <p>Failed to load services. Please try again later.</p>
      </div>
    );
  }

  // Desktop Grid Layout
  const DesktopView = () => (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-2xl font-bold text-gray-700">EXPLORE</h2>
        <a href="/category/all-services" className="flex items-center text-[#174568] font-medium hover:underline">
          See All <img src={vectorMin} className="ml-2" alt="See all" />
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {isLoading
          ? Array.from({ length: 8 }).map((_, index) => <SkeletonCard key={index} />)
          : gigs.map(gig => <ServiceCard key={gig._id} gig={gig} />)
        }
      </div>

      {!isLoading && gigs.length === 0 && (
         <div className="text-center py-10 text-gray-500 col-span-full">
           <p>No services found at the moment.</p>
         </div>
      )}

      {!isLoading && gigs.length > 0 && renderPagination()}
    </div>
  );

  // Mobile Grid Layout
  const MobileView = () => (
    <div className="px-4 py-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-gray-700">EXPLORE</h2>
        <a href="/category/all-services" className="flex items-center text-[#174568] text-sm hover:underline">
          See All <img src={vectorMin} className="ml-2" alt="See all" />
        </a>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {isLoading
          ? Array.from({ length: 4 }).map((_, index) => <SkeletonCard key={index} />)
          : gigs.map(gig => <ServiceCard key={gig._id} gig={gig} />)
        }
      </div>

      {!isLoading && gigs.length === 0 && (
         <div className="text-center py-8 text-gray-500 col-span-full">
           <p>No services found.</p>
         </div>
      )}

      {!isLoading && gigs.length > 0 && renderPagination()}
    </div>
  );

  return isMobile ? <MobileView /> : <DesktopView />;
};

export default ExploreServicesGrid;